/**
 * SelectOrOtherEdit — preset dropdown + "Other…" → free-text input.
 *
 * DLD §C: Used for physicalOfficeRequired (Yes/No/Other) and physicalOfficeType.
 * Saves the preset value or typed text. Empty "Other" → null (does not save empty string).
 */
import { useState, useEffect } from "react";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";

export interface SelectOrOtherOption {
  value: string;
  label: string;
}

interface SelectOrOtherEditProps {
  /** Current saved value (or null when empty). */
  value: string | null;
  /** Preset options. The "Other…" option is appended automatically. */
  options: SelectOrOtherOption[];
  /** Whether the user canEdit. Read-only when false. */
  canEdit: boolean;
  /** Accessible label for the control. */
  ariaLabel: string;
  /** Called with the new value to save (null = clear). */
  onSave: (value: string | null) => void;
  /** Disable while mutation pending. */
  disabled?: boolean;
  /** Locale placeholder. */
  placeholder?: string;
}

const OTHER_SENTINEL = "__other__";

export function SelectOrOtherEdit({
  value,
  options,
  canEdit,
  ariaLabel,
  onSave,
  disabled = false,
  placeholder,
}: SelectOrOtherEditProps): JSX.Element {
  const { t } = useTranslation("common");
  const [editing, setEditing] = useState(false);
  const [selectVal, setSelectVal] = useState<string>(() => {
    if (!value) return "";
    const match = options.find((o) => o.value === value);
    return match ? value : OTHER_SENTINEL;
  });
  const [otherText, setOtherText] = useState<string>(() => {
    if (!value) return "";
    const match = options.find((o) => o.value === value);
    return match ? "" : (value ?? "");
  });

  // Sync when value changes from outside (e.g. refetch)
  useEffect(() => {
    if (!editing) {
      if (!value) {
        setSelectVal("");
        setOtherText("");
      } else {
        const match = options.find((o) => o.value === value);
        setSelectVal(match ? value : OTHER_SENTINEL);
        setOtherText(match ? "" : value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editing]);

  const displayLabel = value
    ? (options.find((o) => o.value === value)?.label ?? value)
    : null;

  const handleSave = () => {
    let finalValue: string | null = null;
    if (selectVal === OTHER_SENTINEL) {
      finalValue = otherText.trim() || null;
    } else if (selectVal) {
      finalValue = selectVal;
    }
    onSave(finalValue);
    setEditing(false);
  };

  const handleCancel = () => {
    // Reset to current persisted value
    if (!value) {
      setSelectVal("");
      setOtherText("");
    } else {
      const match = options.find((o) => o.value === value);
      setSelectVal(match ? value : OTHER_SENTINEL);
      setOtherText(match ? "" : value);
    }
    setEditing(false);
  };

  if (!canEdit) {
    return (
      <span className={cn("text-[14px] font-normal text-text")}>
        {displayLabel ?? <span className="text-dim">—</span>}
      </span>
    );
  }

  if (!editing) {
    return (
      <div className="group flex items-center gap-2 min-h-[34px]">
        <span className="text-[14px] font-normal text-text">
          {displayLabel ?? <span className="text-dim">—</span>}
        </span>
        <button
          type="button"
          aria-label={`Edit ${ariaLabel}`}
          onClick={() => setEditing(true)}
          className={cn(
            "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
            "inline-flex items-center justify-center h-[28px] w-[28px] rounded-md",
            "text-dim hover:text-accent",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
            "min-h-[44px] sm:min-h-[28px]",
          )}
        >
          <svg className="h-[13px] w-[13px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 20h4l10-10-4-4L4 16z" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 py-1">
      <select
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={selectVal}
        onChange={(e) => setSelectVal(e.target.value)}
        aria-label={ariaLabel}
        disabled={disabled}
        className={cn(
          "h-9 px-3 rounded-lg border border-border bg-surface-2 text-base text-text",
          "focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none",
          "min-h-[44px] sm:min-h-[36px] sm:h-9",
        )}
        onKeyDown={(e) => {
          if (e.key === "Escape") handleCancel();
        }}
      >
        <option value="">{placeholder ?? t("select_placeholder", { defaultValue: "Select…" })}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        <option value={OTHER_SENTINEL}>{t("other_option", { defaultValue: "Other…" })}</option>
      </select>

      {selectVal === OTHER_SENTINEL && (
        <input
          type="text"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder={t("other_placeholder", { defaultValue: "Enter value…" })}
          aria-label={`${ariaLabel} — custom value`}
          disabled={disabled}
          className={cn(
            "h-9 px-3 rounded-lg border border-border bg-surface-2 text-base text-text",
            "focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none",
            "min-h-[44px] sm:min-h-[36px] sm:h-9",
          )}
          onKeyDown={(e) => {
            if (e.key === "Escape") handleCancel();
            if (e.key === "Enter") handleSave();
          }}
        />
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={handleSave}
          className={cn(
            "inline-flex h-9 items-center gap-1 rounded-lg bg-accent px-3",
            "text-[12.5px] font-semibold text-white hover:bg-[#4338ca]",
            "disabled:opacity-50 min-h-[44px] sm:min-h-[34px]",
            "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
          )}
        >
          <Check className="h-3.5 w-3.5" aria-hidden /> {t("save")}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className={cn(
            "inline-flex h-9 items-center gap-1 rounded-lg border border-border bg-surface px-3",
            "text-[12.5px] font-semibold text-text hover:bg-surface-2",
            "min-h-[44px] sm:min-h-[34px]",
            "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden /> {t("cancel")}
        </button>
      </div>
    </div>
  );
}
